import * as React from "react";
import { styled } from "styled-components";
import { AutocompleteField, Subheading } from "@wayflyer/flyui";
import { useIntl } from "react-intl";

import type { MetricReferenceDirection } from "../shared";
import { MetricCard } from "../metric-card/metric-card";
import { Spinner } from "../styled";

interface PinnedMetricsHeaderProps {
  metrics: PinnedMetric[];
  title: string;
  dropdownLabel?: string;
}

export interface PinnedMetricsProps {
  metrics: PinnedMetric[];
  title: string;
  loading?: boolean;
  dropdownLabel?: string;
}

export type PinnedMetric = {
  metricType: string;
  title: string;
  metricValue?: string;
  metricReferenceValue?: string;
  metricReferenceDirection?: MetricReferenceDirection;
  metricReferenceSuccess?: boolean;
  metricReferenceText?: string;
  tooltip?: string;
  enabled: boolean;
  onEnable: (enabled: boolean) => void;
};

const Container = styled.div``;
const MultiselectContainer = styled.div`
  display: flex;
  min-width: 80px;
  width: 200px;
`;
const TitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 20px;
`;

const CardsGrid = styled.div`
  display: flex;
  flex-flow: row wrap;
  row-gap: 10px;
  column-gap: 20px;
  justify-content: start;
  align-items: baseline;
`;

export const PinnedMetricsSelector = ({
  metrics,
  dropdownLabel,
  wrapSelection = false,
}: Pick<PinnedMetricsHeaderProps, "metrics" | "dropdownLabel"> & { wrapSelection?: boolean }): UIElement => {
  const intl = useIntl();
  const msbLabel =
    dropdownLabel ??
    intl.formatMessage({ id: "Pinned metrics default message", defaultMessage: "Choose pinned metrics" });
  return (
    <AutocompleteField
      name="pinned-metrics"
      label={wrapSelection ? msbLabel : undefined}
      placeholder={msbLabel}
      multi={wrapSelection ? true : "nowrap"}
      value={metrics.filter((metric) => metric.enabled).map((metric) => metric.metricType)}
      clearable={false}
      disabled={!metrics.length}
      options={metrics.map((metric) => ({ id: metric.metricType, text: metric.title, subText: metric.tooltip }))}
      onChange={(values) => {
        metrics.forEach((metric) => {
          const enabled = values.includes(metric.metricType);
          if (enabled !== metric.enabled) {
            metric.onEnable(enabled);
          }
        });
      }}
    />
  );
};

const PinnedMetricsHeader = ({ metrics, title, dropdownLabel }: PinnedMetricsHeaderProps): UIElement => {
  const msb = (
    <MultiselectContainer>
      <PinnedMetricsSelector metrics={metrics} dropdownLabel={dropdownLabel} />
    </MultiselectContainer>
  );

  return title ? (
    <Container>
      <TitleRow>
        <Subheading>{title}</Subheading>
        {msb}
      </TitleRow>
    </Container>
  ) : (
    <TitleRow> {msb} </TitleRow>
  );
};

/**
 * PinnedMetrics component
 */
export const PinnedMetrics = ({
  metrics,
  dropdownLabel,
  title = "",
  loading = false,
}: PinnedMetricsProps): UIElement => {
  if (!metrics.length) {
    return <Spinner />;
  }
  return (
    <Container>
      <PinnedMetricsHeader metrics={metrics} title={title} dropdownLabel={dropdownLabel} />
      <CardsGrid>
        {metrics.map((metric, index) => {
          if (!metric.enabled) {
            return null;
          }
          return (
            <MetricCard
              key={`metric_card_${metric.metricValue}_${index}`}
              title={metric.title}
              metricValue={metric.metricValue}
              metricReferenceValue={metric.metricReferenceValue}
              metricReferenceDirection={metric.metricReferenceDirection}
              metricReferenceSuccess={metric.metricReferenceSuccess}
              metricReferenceText={metric.metricReferenceText}
              tooltip={metric.tooltip}
              loading={loading}
            />
          );
        })}
      </CardsGrid>
    </Container>
  );
};
